import { getDanhSachPhangNhomHang } from '@/apis/nhomHangApi'
import routerConstants from '@/constants/routerConstants'
import { captureNode } from '@/modules/test-module-chitiet/tester/captureShot'
import {
  auditFilterPopover,
  auditListChrome,
  auditNhomHangForm,
  findButton,
  inputByLabel,
  inspectFormChrome,
  reviewToResult,
  sidebarOf,
  type FormChromeReport,
} from '@/modules/test-module-chitiet/tester/layoutAudit'
import type { TesterShot } from '@/modules/test-module-chitiet/tester/shotStore'
import type { UiTestScenarioSummary } from '@/modules/ui-test/uiTest.types'
import {
  clickElement,
  pressEnter,
  typeSlowly,
  waitFor,
  waitForDialogClosed,
  waitForTopDialog,
} from '@/modules/ui-test/utils/uiTestDomActions'
import { runUiTestSuite, type UiTestFeatureDef, type UiTestRunner } from '@/modules/ui-test/utils/uiTestRunner'

const LIST_PATH = routerConstants.nhomHang.list


export type NhomHangVisualRun = {
  summary: UiTestScenarioSummary
  shots: TesterShot[]
}

function screenOf(uiTest: UiTestRunner): HTMLElement {
  const screen = uiTest.screenRootRef.current
  if (!screen) throw new Error('Không thấy vùng nội dung màn hình')
  return screen
}

async function goCleanList(uiTest: UiTestRunner): Promise<void> {
  // Bỏ query (kể cả uiTester) để lọc sạch — VisualTesterHost giữ locked nên không dừng.
  if (window.location.pathname !== LIST_PATH || window.location.search) {
    uiTest.navigate(LIST_PATH)
    await waitFor(
      () => window.location.pathname === LIST_PATH && window.location.search === '',
      8000,
      100,
    )
  }
  await waitFor(() => {
    const screen = uiTest.screenRootRef.current
    if (!screen?.querySelector('table')) return false
    // Bỏ qua progressbar của banner tester.
    const listLoading = Array.from(screen.querySelectorAll('[role="progressbar"]')).some(
      (bar) => !bar.closest('[data-ui-test-overlay="true"]'),
    )
    if (listLoading) return false
    if (probeRow(screen)) return true
    const text = screen.textContent ?? ''
    return text.includes('Không có dữ liệu') || text.includes('Tổng số 0')
  }, 20_000, 200)
}

function tableHasCode(screen: HTMLElement, code: string): boolean {
  return Array.from(screen.querySelectorAll('tbody tr')).some((row) => (row.textContent ?? '').includes(code))
}

function probeRow(screen: HTMLElement): { row: HTMLTableRowElement; code: string; name: string } | null {
  const table = screen.querySelector('table')
  if (!table) return null
  const headers = Array.from(table.querySelectorAll('thead th')).map((th) =>
    (th.textContent ?? '').replace(/\s+/g, ' ').trim(),
  )
  const codeIdx = headers.findIndex((h) => h === 'Mã nhóm')
  const nameIdx = headers.findIndex((h) => h === 'Tên nhóm')
  if (codeIdx < 0 || nameIdx < 0) return null

  const rows = table.querySelectorAll('tbody tr')
  for (const row of Array.from(rows)) {
    const cells = row.querySelectorAll('td')
    if (cells.length <= Math.max(codeIdx, nameIdx)) continue
    // Bỏ dòng tổng (thường 1 ô colspan).
    if (cells.length < 4) continue
    const code = cells[codeIdx]?.textContent?.replace(/\s+/g, ' ').trim() ?? ''
    const name = cells[nameIdx]?.textContent?.replace(/\s+/g, ' ').trim() ?? ''
    if (!code || code === '—' || code.startsWith('Tổng')) continue
    return { row: row as HTMLTableRowElement, code, name }
  }
  return null
}

/** Ưu tiên dòng còn nút Thêm cấp dưới (chưa max cấp). */
function probeRowForAddChild(
  screen: HTMLElement,
): { row: HTMLTableRowElement; code: string; name: string } | null {
  const table = screen.querySelector('table')
  if (!table) return null
  const headers = Array.from(table.querySelectorAll('thead th')).map((th) =>
    (th.textContent ?? '').replace(/\s+/g, ' ').trim(),
  )
  const codeIdx = headers.findIndex((h) => h === 'Mã nhóm')
  const nameIdx = headers.findIndex((h) => h === 'Tên nhóm')
  if (codeIdx < 0 || nameIdx < 0) return null

  for (const row of Array.from(table.querySelectorAll('tbody tr'))) {
    const cells = row.querySelectorAll('td')
    if (cells.length < 4) continue
    const code = cells[codeIdx]?.textContent?.replace(/\s+/g, ' ').trim() ?? ''
    const name = cells[nameIdx]?.textContent?.replace(/\s+/g, ' ').trim() ?? ''
    if (!code || code === '—' || code.startsWith('Tổng')) continue
    const addChild = row.querySelector('button[aria-label="Thêm cấp dưới"]')
    if (!addChild) continue
    return { row: row as HTMLTableRowElement, code, name }
  }
  return null
}

function searchInput(screen: HTMLElement): HTMLInputElement | null {
  return screen.querySelector('input[placeholder="Theo mã, tên nhóm"]') as HTMLInputElement | null
}

function searchBar(input: HTMLElement): HTMLElement {
  let node: HTMLElement | null = input
  while (node) {
    if (node.querySelector('button[aria-label="Lọc"]')) return node
    node = node.parentElement
  }
  throw new Error('Không thấy thanh tìm kiếm')
}

function topDialog(): HTMLElement | null {
  const dialogs = document.querySelectorAll('[role="dialog"]')
  return (dialogs[dialogs.length - 1] as HTMLElement | undefined) ?? null
}

function parentText(dialog: HTMLElement): string {
  return inputByLabel(dialog, 'Nhóm cha')?.value.replace(/\s+/g, ' ').trim() ?? ''
}

function assertParentEmpty(dialog: HTMLElement, which: string): string {
  const parent = inputByLabel(dialog, 'Nhóm cha')
  if (!parent) return `Cần sửa: ${which} không có ô nhóm cha.`
  const shown = parent.value.replace(/\s+/g, ' ').trim()
  if (shown) return `Cần sửa: ${which} phải để trống nhóm cha, nhưng ô đang có "${shown}".`
  return `${which}: ô nhóm cha để trống.`
}

async function openCreateForm(
  uiTest: UiTestRunner,
): Promise<{ status: 'ok'; dialog: HTMLElement } | { status: 'fail'; message: string }> {
  await goCleanList(uiTest)
  const add = findButton(screenOf(uiTest), 'Thêm nhóm hàng')
  if (!add) return { status: 'fail', message: 'Không thấy nút Thêm nhóm hàng' }
  await clickElement(add)
  const dialog = await waitForTopDialog()
  return { status: 'ok', dialog }
}

async function fillCreateIdentity(dialog: HTMLElement, name: string): Promise<string[]> {
  const issues: string[] = []
  const ma = dialog.querySelector('input[name="maNhom"]') as HTMLInputElement | null
  const ten = dialog.querySelector('input[name="tenNhom"]') as HTMLInputElement | null
  if (!ma) issues.push('Không thấy ô Mã nhóm')
  else if (!ma.disabled) issues.push('Ô Mã nhóm phải khóa (tự sinh), nhưng đang cho nhập')
  if (!ten) {
    issues.push('Không thấy ô Tên nhóm')
    return issues
  }
  await clickElement(ten)
  await typeSlowly(ten, name)
  const shown = ten.value.replace(/\s+/g, ' ').trim()
  if (shown !== name) issues.push(`Gõ tên "${name}" nhưng ô chỉ còn "${shown || '(trống)'}"`)
  return issues
}

function rowByCode(screen: HTMLElement, code: string): HTMLTableRowElement | null {
  const table = screen.querySelector('table')
  if (!table) return null
  const headers = Array.from(table.querySelectorAll('thead th')).map((th) =>
    (th.textContent ?? '').replace(/\s+/g, ' ').trim(),
  )
  const codeIdx = headers.findIndex((h) => h === 'Mã nhóm')
  if (codeIdx < 0) return null
  for (const row of Array.from(table.querySelectorAll('tbody tr'))) {
    const cell = row.querySelectorAll('td')[codeIdx]
    if ((cell?.textContent ?? '').replace(/\s+/g, ' ').trim() === code) return row as HTMLTableRowElement
  }
  return null
}

async function openEditForCode(
  uiTest: UiTestRunner,
  code: string,
): Promise<{ status: 'ok'; dialog: HTMLElement } | { status: 'fail'; message: string }> {
  await goCleanList(uiTest)
  const input = searchInput(screenOf(uiTest))
  if (!input) return { status: 'fail', message: 'Không thấy ô tìm để mở dòng sửa' }
  await clickElement(input)
  await typeSlowly(input, code)
  input.focus()
  await pressEnter(input)
  try {
    await waitFor(() => rowByCode(screenOf(uiTest), code) != null, 8000, 150)
  } catch {
    return { status: 'fail', message: `Tìm "${code}" quá thời gian, không thấy dòng để sửa` }
  }
  const edit = rowByCode(screenOf(uiTest), code)?.querySelector('button[aria-label="Chỉnh sửa"]') as HTMLButtonElement | null
  if (!edit) return { status: 'fail', message: `Dòng ${code} không có nút Sửa` }
  await clickElement(edit)
  const dialog = await waitForTopDialog()
  try {
    await waitFor(() => {
      const ma = dialog.querySelector('input[name="maNhom"]') as HTMLInputElement | null
      return ma?.value.replace(/\s+/g, ' ').trim() === code
    }, 12_000, 150)
  } catch {
    return { status: 'fail', message: `Form sửa ${code} không hiện mã trong thời gian chờ` }
  }
  return { status: 'ok', dialog }
}

async function waitParentShown(dialog: HTMLElement, code: string): Promise<string | null> {
  try {
    await waitFor(() => parentText(dialog).length > 0, 8000, 150)
    return null
  } catch {
    return `Cần sửa: nhóm ${code} đang có nhóm cha nhưng form sửa không hiện nhóm cha.`
  }
}

async function closeTopDialog(): Promise<void> {
  if (document.querySelector('[role="listbox"]')) {
    document.body.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }))
    await waitFor(() => document.querySelector('[role="listbox"]') == null, 2000, 80).catch(() => undefined)
  }
  const dialog = await waitForTopDialog(4000)
  const cancel = findButton(dialog, 'Hủy') ?? findButton(dialog, 'Đóng')
  const closeIcon = dialog.querySelector('button[aria-label="Đóng"]') as HTMLButtonElement | null
  const btn = cancel ?? closeIcon
  if (!btn) throw new Error('Không thấy nút đóng hộp thoại')
  await clickElement(btn)
  await waitForDialogClosed(dialog, 8000)
}

async function pickSelectOption(combo: HTMLElement, optionText: string): Promise<void> {
  combo.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  // MUI Select mở listbox bằng mousedown trên .MuiSelect-select, không chỉ click().
  const rect = combo.getBoundingClientRect()
  const at = {
    bubbles: true,
    cancelable: true,
    view: window,
    clientX: rect.left + Math.min(24, rect.width / 2),
    clientY: rect.top + rect.height / 2,
  }
  combo.focus()
  combo.dispatchEvent(new MouseEvent('mousedown', at))
  combo.dispatchEvent(new MouseEvent('mouseup', at))
  combo.dispatchEvent(new MouseEvent('click', at))
  try {
    await waitFor(() => document.querySelectorAll('[role="listbox"] [role="option"]').length > 0, 4000, 80)
  } catch {
    // Thử native click nếu synthetic không mở được.
    combo.click()
    await waitFor(() => document.querySelectorAll('[role="listbox"] [role="option"]').length > 0, 4000, 80)
  }
  const option = Array.from(document.querySelectorAll('[role="option"]')).find((node) => {
    const text = node.textContent?.replace(/\s+/g, ' ').trim() ?? ''
    return text === optionText || text.includes(optionText)
  }) as HTMLElement | undefined
  if (!option) throw new Error(`Không thấy lựa chọn "${optionText}"`)
  await clickElement(option)
  await waitFor(() => document.querySelector('[role="listbox"]') == null, 4000, 80)
}

function evaluateShortForm(which: 'thêm' | 'sửa', chrome: FormChromeReport, base?: FormChromeReport): string {
  const fields = chrome.fieldLabels.join(', ') || 'không đọc được nhãn'
  const issues: string[] = []
  if (chrome.scrolls.length) {
    issues.push(`có ${chrome.fieldLabels.length || 3} ô (${fields}) nhưng phải cuộn — ${chrome.scrolls.join('; ')}`)
  }
  if (!chrome.footerOutsideBody) {
    issues.push('nút Hủy/Lưu nằm trong thân form, chưa theo base form (footer cố định ngoài vùng nhập)')
  }
  if (which === 'sửa' && base && base.scrolls.length === 0 && chrome.scrolls.length > 0) {
    issues.push('form thêm cùng số ô thì không cuộn')
  }
  if (issues.length) return `Cần sửa: Form ${which} — ${issues.join('. ')}.`
  return `Form ${which} theo base: [${fields}], xếp dọc, không phải cuộn, Hủy/Lưu ở footer.`
}

/** Gộp kết quả thao tác + đánh giá — fail nếu bất kỳ phần nào có "Cần sửa". */
function judgeParts(
  base: { status: 'ok' | 'skip' | 'fail'; message: string },
  parts: string[],
): { status: 'ok' | 'skip' | 'fail'; message: string; evaluation: string } {
  const evaluation = parts.filter(Boolean).join(' ')
  if (base.status === 'skip') return { ...base, evaluation }
  const needsFix = parts.some((text) => text.includes('Cần sửa')) || base.status === 'fail'
  return {
    status: needsFix ? 'fail' : 'ok',
    message: needsFix
      ? parts.find((text) => text.includes('Cần sửa')) || base.message
      : base.message,
    evaluation,
  }
}

function withShot<T extends { status: 'ok' | 'skip' | 'fail'; message: string }>(
  interaction: T,
  shot: TesterShot | null,
): T {
  if (interaction.status === 'skip') return interaction
  if (!shot?.dataUrl) {
    return {
      ...interaction,
      status: 'fail' as const,
      message: `${interaction.message} Không chụp được ảnh${shot?.error ? `: ${shot.error}` : ''}.`,
    }
  }
  return interaction
}

export async function runNhomHangVisualTest(uiTest: UiTestRunner): Promise<NhomHangVisualRun> {
  const shots: TesterShot[] = []
  let createChrome: FormChromeReport | null = null

  const snap = async (node: HTMLElement, label: string): Promise<TesterShot> => {
    const shot = await captureNode(node, label)
    shots.push(shot)
    return shot
  }

  const features: UiTestFeatureDef[] = [
    {
      id: 'list-layout',
      label: 'Danh sách',
      run: async () => {
        await goCleanList(uiTest)
        const screen = screenOf(uiTest)
        const review = auditListChrome(screen)
        const shot = await snap(screen, 'Danh sách nhóm hàng')
        const judged = reviewToResult(review, 'Bảng, ô tìm, nút thêm và sidebar đang hiện')
        return withShot(judged, shot)
      },
    },
    {
      id: 'search',
      label: 'Ô tìm kiếm',
      run: async () => {
        await goCleanList(uiTest)
        const screen = screenOf(uiTest)
        const probe = probeRow(screen)
        if (!probe) return { status: 'skip' as const, message: 'Không có dòng để gõ tìm' }
        const input = searchInput(screen)
        if (!input) return { status: 'fail' as const, message: 'Không thấy ô tìm' }
        await clickElement(input)
        await typeSlowly(input, probe.code)
        input.focus()
        await pressEnter(input)
        await waitFor(() => window.location.search.includes('searchQuery='), 8000, 150)
        await waitFor(() => tableHasCode(screenOf(uiTest), probe.code), 8000, 150)
        const after = screenOf(uiTest)
        const shot = await snap(after, `Tìm "${probe.code}"`)
        const query = new URLSearchParams(window.location.search).get('searchQuery') ?? ''
        if (query !== probe.code) {
          return withShot({ status: 'fail', message: `Enter không lọc đúng mã (URL searchQuery="${query}")` }, shot)
        }
        if (!tableHasCode(after, probe.code)) {
          return withShot(
            { status: 'fail', message: `Sau khi gõ "${probe.code}" không thấy dòng đó trên bảng` },
            shot,
          )
        }
        const clear = searchBar(searchInput(after) ?? input).querySelector(
          'button[aria-label="Đặt lại"]',
        ) as HTMLButtonElement | null
        if (clear) await clickElement(clear)
        return withShot(
          { status: 'ok', message: `Gõ "${probe.code}", Enter, bảng còn đúng dòng, rồi xóa ô tìm` },
          shot,
        )
      },
    },
    {
      id: 'filter-popover',
      label: 'Bộ lọc',
      run: async () => {
        await goCleanList(uiTest)
        const screen = screenOf(uiTest)
        const probe = probeRow(screen)
        const input = searchInput(screen)
        if (!input) return { status: 'fail' as const, message: 'Không thấy ô tìm để mở bộ lọc' }
        const filterBtn = searchBar(input).querySelector('button[aria-label="Lọc"]') as HTMLButtonElement | null
        if (!filterBtn) return { status: 'fail' as const, message: 'Không thấy nút Lọc' }
        await clickElement(filterBtn)
        await waitFor(() => Boolean(document.querySelector('.MuiPopover-paper')), 5000, 80)
        const paper = document.querySelector('.MuiPopover-paper') as HTMLElement
        const emptyReview = auditFilterPopover(paper)
        const emptyShot = await snap(paper, 'Popover lọc')
        if (emptyReview.hard.length) {
          return withShot(reviewToResult(emptyReview, ''), emptyShot)
        }
        const fields = Array.from(paper.querySelectorAll('input')) as HTMLInputElement[]
        const sample = await loadFilterSample(probe)
        await typeSlowly(fields[0], sample.ma)
        await typeSlowly(fields[1], sample.ten)
        await typeSlowly(fields[2], sample.maCha)
        await typeSlowly(fields[3], sample.tenCha)
        const filledPaper = (document.querySelector('.MuiPopover-paper') as HTMLElement | null) ?? paper
        const filledValues = Array.from(filledPaper.querySelectorAll('input')).map((input) => input.value.trim())
        const emptySlots = ['Mã nhóm', 'Tên nhóm', 'Mã nhóm cha', 'Nhóm cha'].filter((_, index) => !filledValues[index])
        const filledShot = await snap(filledPaper, 'Popover lọc đã nhập đủ 4 ô')
        if (emptySlots.length) {
          return {
            ...withShot({ status: 'fail', message: `Chưa nhập hết bộ lọc bảng: ${emptySlots.join(', ')}` }, filledShot),
            evaluation: `Cần sửa: Popover lọc bảng còn trống ${emptySlots.join(', ')}. Đã gõ mã "${sample.ma}", tên "${sample.ten}", mã cha "${sample.maCha}", nhóm cha "${sample.tenCha}".`,
          }
        }
        const searchBtn = findButton(filledPaper, 'Tìm kiếm')
        if (!searchBtn) return withShot({ status: 'fail', message: 'Không thấy nút Tìm kiếm trong popover' }, filledShot)
        await clickElement(searchBtn)
        await waitFor(() => window.location.search.includes('maNhom='), 8000, 150)
        const params = new URLSearchParams(window.location.search)
        const missingParams = [
          ['maNhom', sample.ma],
          ['tenNhom', sample.ten],
          ['maNhomCha', sample.maCha],
          ['tenNhomCha', sample.tenCha],
        ].filter(([key, value]) => params.get(key) !== value)
        const resultShot = await snap(screenOf(uiTest), 'Kết quả lọc đủ 4 ô')
        const shot = resultShot.dataUrl ? resultShot : filledShot
        if (missingParams.length) {
          return {
            ...withShot({ status: 'fail', message: 'Bấm Tìm kiếm nhưng URL thiếu ô đã nhập' }, shot),
            evaluation: `Cần sửa: Đã nhập đủ 4 ô lọc nhưng URL không giữ ${missingParams.map(([key]) => key).join(', ')}.`,
          }
        }
        const stillThere = !sample.expectRow || tableHasCode(screenOf(uiTest), sample.ma)
        const filledText = `Đã nhập hết 4 ô lọc bảng: mã "${sample.ma}", tên "${sample.ten}", mã nhóm cha "${sample.maCha}", tên nhóm cha "${sample.tenCha}".`
        return {
          ...withShot(
            {
              status: stillThere ? 'ok' : 'fail',
              message: stillThere ? `Đã nhập đủ 4 ô lọc, bảng còn dòng ${sample.ma}` : `Lọc đủ 4 ô nhưng mất dòng ${sample.ma}`,
            },
            shot,
          ),
          evaluation: stillThere
            ? `${filledText} Bảng còn đúng dòng.`
            : `Cần sửa: ${filledText} Bảng không còn dòng "${sample.ma}".`,
        }
      },
    },
    {
      id: 'status-filter',
      label: 'Lọc trạng thái',
      run: async () => {
        await goCleanList(uiTest)
        const sidebar = sidebarOf(screenOf(uiTest))
        if (!sidebar) return { status: 'fail' as const, message: 'Không thấy sidebar lọc' }
        const activeLabel = labelOf(sidebar, 'Đang hoạt động')
        const activeRadio = sidebar.querySelector(
          'input[type="radio"][value="DANG_HOAT_DONG"]',
        ) as HTMLInputElement | null
        const active = activeLabel ?? activeRadio
        if (!active) return { status: 'fail' as const, message: 'Sidebar thiếu lựa chọn Đang hoạt động' }
        await clickElement(active)
        await waitFor(() => window.location.search.includes('trangThai=DANG_HOAT_DONG'), 8000, 150)
        const shot = await snap(screenOf(uiTest), 'Lọc đang hoạt động')
        const allLabel = labelOf(sidebarOf(screenOf(uiTest)) ?? sidebar, 'Tất cả')
        const allRadio = (sidebarOf(screenOf(uiTest)) ?? sidebar).querySelector(
          'input[type="radio"][value="all"]',
        ) as HTMLInputElement | null
        const all = allLabel ?? allRadio
        if (all) await clickElement(all)
        return withShot(
          { status: 'ok', message: 'Chọn radio Đang hoạt động, bảng lọc theo URL, rồi chọn Tất cả' },
          shot,
        )
      },
    },
    {
      id: 'tree-filter',
      label: 'Cây nhóm',
      run: async () => {
        await goCleanList(uiTest)
        const sidebar = sidebarOf(screenOf(uiTest))
        if (!sidebar) return { status: 'fail' as const, message: 'Không thấy sidebar' }
        const treeBtn = Array.from(sidebar.querySelectorAll('button')).find((btn) =>
          /\(\d+\)\s*$/.test((btn.textContent ?? '').trim()),
        ) as HTMLButtonElement | undefined
        if (!treeBtn) return { status: 'skip' as const, message: 'Sidebar chưa có nút nhóm trên cây' }
        const label = (treeBtn.textContent ?? '').replace(/\s+/g, ' ').trim()
        await clickElement(treeBtn)
        await waitFor(() => window.location.search.includes('nhomHangId='), 8000, 150)
        const shot = await snap(screenOf(uiTest), `Cây: ${label}`)
        const reset = findButton(sidebarOf(screenOf(uiTest)) ?? sidebar, 'Đặt lại')
        if (reset) await clickElement(reset)
        return withShot({ status: 'ok', message: `Bấm nhóm "${label}" trên cây, URL có nhomHangId` }, shot)
      },
    },
    {
      id: 'sort-select',
      label: 'Sắp xếp',
      run: async () => {
        await goCleanList(uiTest)
        const sidebar = sidebarOf(screenOf(uiTest))
        if (!sidebar) return { status: 'fail' as const, message: 'Không thấy sidebar sắp xếp' }
        // Chỉ lấy Select của khối sắp xếp — tránh nhầm combobox khác.
        const sortSection =
          Array.from(sidebar.querySelectorAll('.MuiFormControl-root, [class*="Stack"]')).find((el) =>
            (el.textContent ?? '').includes('Sắp xếp theo'),
          ) ?? sidebar
        const combos = Array.from(
          sortSection.querySelectorAll('.MuiSelect-select'),
        ) as HTMLElement[]
        const fieldCombo =
          combos[0] ??
          (sidebar.querySelector('.MuiSelect-select') as HTMLElement | null)
        if (!fieldCombo) return { status: 'fail' as const, message: 'Không thấy select Sắp xếp theo' }
        await pickSelectOption(fieldCombo, 'Mã nhóm')
        await waitFor(() => window.location.search.includes('sortField=code'), 8000, 150)
        await waitFor(() => {
          const next = sidebarOf(screenOf(uiTest))?.querySelectorAll('.MuiSelect-select')[1] as
            | HTMLElement
            | undefined
          return Boolean(next && next.getAttribute('aria-disabled') !== 'true')
        }, 5000, 100)
        const dir = sidebarOf(screenOf(uiTest))?.querySelectorAll('.MuiSelect-select')[1] as
          | HTMLElement
          | undefined
        if (!dir) return { status: 'fail' as const, message: 'Không thấy select chiều sắp xếp' }
        await pickSelectOption(dir, 'Giảm dần')
        await waitFor(() => window.location.search.includes('sortOrder=desc'), 8000, 150)
        const shot = await snap(screenOf(uiTest), 'Sắp xếp mã giảm dần')
        return withShot({ status: 'ok', message: 'Chọn Sắp xếp theo Mã nhóm, chiều Giảm dần' }, shot)
      },
    },
    {
      id: 'create-without-parent',
      label: 'Thêm không nhóm cha',
      run: async () => {
        const opened = await openCreateForm(uiTest)
        if (opened.status !== 'ok') return opened
        const dialog = opened.dialog
        createChrome = inspectFormChrome(dialog)
        const emptyReview = auditNhomHangForm(dialog, 'create')
        await snap(dialog, 'Form thêm trống')
        const fillIssues = await fillCreateIdentity(dialog, 'Nhóm không cha UIT')
        const parentEval = assertParentEmpty(dialog, 'Form thêm không nhóm cha')
        const filled = topDialog() ?? dialog
        const filledShot = await snap(filled, 'Thêm nhóm không có nhóm cha')
        await closeTopDialog()
        const layoutEval = evaluateShortForm('thêm', createChrome)
        const fillEval = fillIssues.length
          ? `Cần sửa: ${fillIssues.join('; ')}.`
          : 'Đã gõ tên nhóm; mã khóa (tự sinh); để trống nhóm cha.'
        const judged = reviewToResult(emptyReview, 'Đã kiểm form thêm, Hủy không lưu')
        return {
          ...withShot(
            judgeParts(judged, [layoutEval, fillEval, parentEval]),
            filledShot,
          ),
        }
      },
    },
    {
      id: 'create-with-parent',
      label: 'Thêm có nhóm cha',
      run: async () => {
        const opened = await openCreateForm(uiTest)
        if (opened.status !== 'ok') return opened
        const dialog = opened.dialog
        const review = auditNhomHangForm(dialog, 'create')
        const fillIssues = await fillCreateIdentity(dialog, 'Nhóm có cha UIT')
        const parent = inputByLabel(dialog, 'Nhóm cha')
        const parentEval = parent
          ? (
              await typeAndChooseParent(parent, async (list) => {
                await snap(list, 'Dropdown nhóm cha khi thêm')
              })
            ).evaluation
          : 'Cần sửa: Form thêm không có ô nhóm cha để chọn.'
        const filled = topDialog() ?? dialog
        const chrome = inspectFormChrome(filled)
        const filledShot = await snap(filled, 'Thêm nhóm đã chọn nhóm cha')
        await closeTopDialog()
        const layoutEval = evaluateShortForm('thêm', chrome, createChrome ?? undefined)
        const fillEval = fillIssues.length
          ? `Cần sửa: ${fillIssues.join('; ')}.`
          : 'Đã gõ tên nhóm; mã khóa (tự sinh).'
        const judged = reviewToResult(review, 'Đã chọn nhóm cha, Hủy không lưu')
        return {
          ...withShot(
            judgeParts(judged, [layoutEval, fillEval, parentEval]),
            filledShot,
          ),
        }
      },
    },
    {
      id: 'detail',
      label: 'Thêm cấp dưới',
      run: async () => {
        await goCleanList(uiTest)
        const probe = probeRowForAddChild(screenOf(uiTest))
        if (!probe) return { status: 'skip' as const, message: 'Không có dòng còn nút Thêm cấp dưới' }
        const addChild = probe.row.querySelector(
          'button[aria-label="Thêm cấp dưới"]',
        ) as HTMLButtonElement | null
        if (!addChild) {
          return {
            status: 'fail' as const,
            message: `Dòng ${probe.code} không có nút Thêm cấp dưới (màn này không có nút Xem chi tiết)`,
          }
        }
        await clickElement(addChild)
        const dialog = await waitForTopDialog()
        await waitFor(() => Boolean(dialog.querySelector('input[name="tenNhom"]')), 8000, 150)
        // Prefill nhóm cha từ dòng nguồn — chờ SearchNhomHang load nhãn.
        try {
          await waitFor(() => parentText(dialog).length > 0, 10_000, 150)
        } catch {
          /* đánh giá bên dưới */
        }
        const review = auditNhomHangForm(dialog, 'create')
        const parentShown = parentText(dialog)
        const shot = await snap(dialog, `Thêm cấp dưới từ ${probe.code}`)
        await closeTopDialog()
        if (!parentShown) {
          return withShot(
            {
              status: 'fail',
              message: `Bấm Thêm cấp dưới từ ${probe.code} nhưng ô Nhóm cha vẫn trống`,
            },
            shot,
          )
        }
        const parentOk =
          parentShown.includes(probe.code) ||
          parentShown.includes(probe.name) ||
          parentShown.toLowerCase().includes(probe.name.toLowerCase())
        if (!parentOk) {
          return withShot(
            {
              status: 'fail',
              message: `Nhóm cha hiện "${parentShown}" — kỳ vọng liên quan ${probe.code} / ${probe.name}`,
            },
            shot,
          )
        }
        const judged = reviewToResult(
          review,
          `Thêm cấp dưới từ ${probe.code}: nhóm cha đã prefill "${parentShown}", rồi Hủy`,
        )
        return withShot(judged, shot)
      },
    },
    {
      id: 'edit-assign-parent',
      label: 'Sửa gán nhóm cha',
      run: async () => {
        const cases = await loadParentCases()
        if (!cases.none) return { status: 'skip' as const, message: 'Không có nhóm hàng nào đang không có nhóm cha' }
        const opened = await openEditForCode(uiTest, cases.none.code)
        if (opened.status !== 'ok') return opened
        const dialog = opened.dialog
        const review = auditNhomHangForm(dialog, 'edit')
        const before = parentText(dialog)
        if (before) review.hard.push(`Nhóm ${cases.none.code} đang không có cha nhưng form đã hiện "${before}"`)
        const parent = inputByLabel(dialog, 'Nhóm cha')
        const parentEval = parent
          ? (
              await typeAndChooseParent(parent, async (list) => {
                await snap(list, `Dropdown gán cha cho ${cases.none?.code}`)
              })
            ).evaluation
          : 'Cần sửa: Form sửa không có ô nhóm cha để gán.'
        const filled = topDialog() ?? dialog
        const chrome = inspectFormChrome(filled)
        const shot = await snap(filled, `Sửa ${cases.none.code}: không cha → có cha`)
        await closeTopDialog()
        const layoutEval = evaluateShortForm('sửa', chrome, createChrome ?? undefined)
        const judged = reviewToResult(review, `Sửa ${cases.none.code}: không nhóm cha → chọn nhóm cha. Đã Hủy, không lưu`)
        return {
          ...withShot(
            judgeParts(judged, [layoutEval, `Nhóm ${cases.none.code} không có cha. ${parentEval}`]),
            shot,
          ),
        }
      },
    },
    {
      id: 'edit-clear-parent',
      label: 'Sửa xóa nhóm cha',
      run: async () => {
        const cases = await loadParentCases()
        if (!cases.has) return { status: 'skip' as const, message: 'Không có nhóm hàng nào đang có nhóm cha' }
        const opened = await openEditForCode(uiTest, cases.has.code)
        if (opened.status !== 'ok') return opened
        const dialog = opened.dialog
        const review = auditNhomHangForm(dialog, 'edit')
        const ready = await waitParentShown(dialog, cases.has.code)
        if (ready) review.hard.push(ready)
        const cleared = await clearParentField(topDialog() ?? dialog)
        const after = topDialog() ?? dialog
        const chrome = inspectFormChrome(after)
        const shot = await snap(after, `Sửa ${cases.has.code}: có cha → không cha`)
        await closeTopDialog()
        const layoutEval = evaluateShortForm('sửa', chrome, createChrome ?? undefined)
        const judged = reviewToResult(review, `Sửa ${cases.has.code}: có nhóm cha → xóa nhóm cha. Đã Hủy, không lưu`)
        return {
          ...withShot(
            judgeParts(judged, [layoutEval, `Nhóm ${cases.has.code} đang có cha. ${cleared.evaluation}`]),
            shot,
          ),
        }
      },
    },
  ]

  const summary = await runUiTestSuite(uiTest, features, 'Nhóm hàng — tester')
  return { summary, shots }
}

async function loadFilterSample(probe: { code: string; name: string } | null): Promise<{
  ma: string
  ten: string
  maCha: string
  tenCha: string
  expectRow: boolean
}> {
  const flat = await getDanhSachPhangNhomHang()
  const byId = new Map(flat.map((row) => [String(row.id), row]))
  const child = flat.find((row) => row.parentId != null && byId.get(String(row.parentId))?.code)
  const parent = child?.parentId != null ? byId.get(String(child.parentId)) : undefined
  if (child && parent?.code) {
    return {
      ma: child.code,
      ten: child.name.slice(0, 24),
      maCha: parent.code,
      tenCha: parent.name || child.parentName || parent.code,
      expectRow: true,
    }
  }
  return {
    ma: probe?.code || flat[0]?.code || 'NH',
    ten: (probe?.name || flat[0]?.name || 'nhóm').slice(0, 24),
    maCha: 'CHA',
    tenCha: 'nhóm cha',
    expectRow: false,
  }
}

async function typeAndChooseParent(
  input: HTMLInputElement,
  onList: (list: HTMLElement) => Promise<void>,
): Promise<{ evaluation: string }> {
  const query = 'a'
  await clickElement(input)
  await typeSlowly(input, query, undefined, { blur: false })
  try {
    await waitFor(() => document.querySelectorAll('[role="listbox"] [role="option"]').length > 0, 6000, 80)
  } catch {
    return { evaluation: `Cần sửa: gõ "${query}" vào nhóm cha nhưng không mở danh sách để chọn.` }
  }
  const list = document.querySelector('[role="listbox"]') as HTMLElement | null
  if (list) await onList(list)
  const option = Array.from(document.querySelectorAll('[role="listbox"] [role="option"]')).find(
    (node) => (node.textContent ?? '').replace(/\s+/g, ' ').trim().length > 0,
  ) as HTMLElement | undefined
  if (!option) return { evaluation: `Cần sửa: gõ "${query}" nhưng danh sách nhóm cha không có dòng.` }
  const label = (option.textContent ?? '').replace(/\s+/g, ' ').trim()
  const fresh =
    (Array.from(document.querySelectorAll('[role="listbox"] [role="option"]')).find(
      (node) => (node.textContent ?? '').replace(/\s+/g, ' ').trim() === label,
    ) as HTMLElement | undefined) ?? option
  await clickElement(fresh)
  await waitFor(() => {
    const dialog = topDialog()
    const shown = dialog ? parentText(dialog) : ''
    return document.querySelector('[role="listbox"]') == null || (shown.length > 0 && shown !== query)
  }, 4000, 80).catch(() => undefined)
  const dialog = topDialog()
  const shown = dialog ? parentText(dialog) : input.value.replace(/\s+/g, ' ').trim()
  if (!shown || shown === query) {
    return { evaluation: `Cần sửa: đã gõ "${query}" và bấm "${label}" nhưng ô nhóm cha chưa nhận lựa chọn.` }
  }
  return { evaluation: `Đã gõ "${query}" để lọc nhóm cha, rồi chọn "${shown}".` }
}

async function clearParentField(dialog: HTMLElement): Promise<{ evaluation: string }> {
  const parent = inputByLabel(dialog, 'Nhóm cha')
  if (!parent) {
    return { evaluation: 'Cần sửa: không thấy ô nhóm cha để xóa.' }
  }
  const before = parent.value.replace(/\s+/g, ' ').trim()
  if (!before) return { evaluation: 'Ô nhóm cha đã trống.' }

  const root = parent.closest('.MuiAutocomplete-root') as HTMLElement | null
  const pressEscape = (el: HTMLElement) => {
    el.dispatchEvent(
      new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', keyCode: 27, which: 27, bubbles: true, cancelable: true }),
    )
  }

  // openOnFocus → Escape đóng list; Escape lần 2 (clearOnEscape) xóa giá trị — đúng thao tác người dùng.
  parent.focus()
  parent.click()
  await new Promise<void>((r) => setTimeout(r, 250))
  pressEscape(parent)
  await waitFor(() => document.querySelector('[role="listbox"]') == null, 2000, 80).catch(() => undefined)
  await new Promise<void>((r) => setTimeout(r, 150))
  pressEscape(parent)

  const cleared = () => {
    const input = inputByLabel(dialog, 'Nhóm cha')
    return Boolean(input && !input.value.replace(/\s+/g, ' ').trim())
  }

  try {
    await waitFor(cleared, 2500, 80)
    return { evaluation: 'Đã xóa nhóm cha (Escape), ô trống.' }
  } catch {
    /* thử nút clear */
  }

  if (root) {
    root.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true }))
    root.classList.add('Mui-focused')
  }
  const clearBtn = root?.querySelector('button.MuiAutocomplete-clearIndicator') as HTMLButtonElement | null
  if (clearBtn) {
    clearBtn.style.cssText += ';visibility:visible!important;opacity:1!important;width:auto!important;height:auto!important'
    clearBtn.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }))
    clearBtn.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }))
    clearBtn.click()
  }

  try {
    await waitFor(cleared, 4000, 80)
    return { evaluation: 'Đã xóa nhóm cha, ô trống.' }
  } catch {
    const still = inputByLabel(dialog, 'Nhóm cha')?.value.replace(/\s+/g, ' ').trim() ?? ''
    return {
      evaluation: `Cần sửa: đã xóa nhóm cha (trước "${before}") nhưng ô vẫn còn "${still || 'giá trị'}".`,
    }
  }
}

async function loadParentCases(): Promise<{
  none: { code: string } | null
  has: { code: string } | null
}> {
  const flat = await getDanhSachPhangNhomHang()
  const none = flat.find((row) => (row.parentId == null || row.parentId === '') && row.code)
  const has = flat.find((row) => row.parentId != null && row.parentId !== '' && row.code)
  return {
    none: none ? { code: none.code } : null,
    has: has ? { code: has.code } : null,
  }
}

function labelOf(root: HTMLElement, text: string): HTMLElement | null {
  const nodes = root.querySelectorAll('label')
  for (const node of Array.from(nodes)) {
    if ((node.textContent ?? '').replace(/\s+/g, ' ').trim() === text) return node as HTMLElement
  }
  return null
}
